import { BookingStatus, InquiryState, PaymentStatus } from './index';
import { ActionTree } from 'vuex';
import { IStore } from "@store/index";
import { logRequest, postDeal, postResponse } from "@store/services/fetchCheckoutData";
import Inquiry, { TourradarInquiry } from "../interfaces/inquiry";
import DealResponse from "../interfaces/dealResponse";
import { CHECKOUT_CREATE_DEAL_LOADING_TEXT } from "@constants/loading";
import { InquiryAssistComments } from "@store/checkout/interfaces/assistComments";
import { toString } from "@store/helper/object";
import { AxiosError, AxiosResponse } from "axios";
import { INVALID } from "@constants/voucher";

export default {
    async handleInquiry({ state, commit, dispatch, rootState }, { comment, voucher, organizerAgreement }) {
        dispatch('createInquiryData', { comment: comment, voucher: voucher, organizerAgreement: organizerAgreement });

        await dispatch('logInquiryAndDataPolicy');
        await dispatch('funnel/saveCheckoutFunnelOnDevice', {}, { root: true });

        if (rootState.checkout.checkoutSettings.bindingMode === 'direct') {
            if (rootState.checkout.hasPayment) {
                commit('setLoadingTexts', CHECKOUT_CREATE_DEAL_LOADING_TEXT);
            }
            await dispatch('createDeal');

            if (!rootState.checkout.hasPayment) {
                return;
            }

            if (rootState.checkout.hasPayment && state.dealResponse.bookingId >= 1) {
                await dispatch('handlePayment');
            } else {
                await dispatch('forwardToErrorPage');
            }
        } else if (shouldCreateInquiry(rootState)) {
            commit('resetTraveller');
            await dispatch('createInquiry');
        }
    },
    async createInquiry({ state, commit, dispatch, rootState }) {
        await postDeal(rootState.checkout.apiEndpoint, 'Inquiry', rootState.checkout.apiToken, state.inquiry, rootState.checkout.checkoutSettings.locale)
            .then(async response => {
                await dispatch('handleDealResponse', response.data)

                if (state.dealResponse.status === BookingStatus.SUCCESS) {
                    commit('setIsFinished', true)
                }
            })
            .catch((error: AxiosError) => {
                const responseStatus = error.response ? error.response.status : 'none';
                const message = `Error in createInquiry | Error code: 1642576733803 | HTTP status: ${responseStatus} | ${error.message}`;

                dispatch('sendErrorMailToTeam', error);
                dispatch('openErrorOverlay');

                throw Error(message);
            });
    },
    async createDeal({ state, commit, dispatch, rootState }) {
        await postDeal(rootState.checkout.apiEndpoint, 'Deal', rootState.checkout.apiToken, state.inquiry, rootState.checkout.checkoutSettings.locale)
            .then(async response => {
                const currentBookingId = state.dealResponse.bookingId;
                await dispatch('handleDealResponse', response.data);

                if(currentBookingId !== response.data.bookingId) {
                    commit('resetAdyenSessionId');
                }

                if (state.dealResponse.status !== BookingStatus.SUCCESS) {
                    return;
                }

                if (!rootState.checkout.hasPayment) {
                    commit('setIsFinished', true);
                }
            })
            .catch((error: AxiosError) => {
                const responseStatus = error.response ? error.response.status : 'none';
                const message = `Error in createDeal | Error code: 1642576742822 | HTTP status: ${responseStatus} | ${error.message}`;

                dispatch('sendErrorMailToTeam', error);
                dispatch('openErrorOverlay');

                throw Error(message);
            })
    },
    handlePayment({ commit, dispatch, rootGetters }) {
        if (rootGetters['checkout/isUK']) {
            dispatch('handlePpsPayment');

            return;
        }

        dispatch('nextNavigationObject', false);
        commit('disableCheckoutSubmitBtn', false);
    },
    async forwardToErrorPage({ commit }) {
        commit('setPaymentStatus', PaymentStatus.FAILED);
        commit('setIsFinished', true);
    },
    async logInquiryAndDataPolicy({ state, dispatch, rootState }) {
        await logRequest(rootState.checkout.internalApiEndpoint, 'loginquiry', state)
            .catch((error: AxiosError) => {
                const responseStatus = error.response ? error.response.status : 'none';
                const message = `Error in logInquiryAndDataPolicy | Error code: 1642576760606 | HTTP status: ${responseStatus} | ${error.message}`;

                dispatch('openErrorOverlay');

                throw Error(message);
            })
    },
    async sendErrorMailToTeam({ state, dispatch, rootState }, error: AxiosError) {
        const payLoad = {
            inquiry: state.inquiry,
            error: error
        };

        await logRequest(rootState.checkout.internalApiEndpoint, 'sendErrorMail', payLoad)
            .catch((error: AxiosError) => {
                const responseStatus = error.response ? error.response.status : 'none';
                const message = `Error in sendErrorMailToTeam | Error code: 1642576767614 | HTTP status: ${responseStatus} | ${error.message}`;

                dispatch('openErrorOverlay');

                throw Error(message);
            })
    },
    createInquiryData({ state, commit, rootState, getters, rootGetters }, { organizerAgreement }) {
        const inquiryComments: InquiryAssistComments = { overviewComment: state.inquiry.comment, ...rootState.checkout.assistComments };

        const rawInquiry = {
            // @ts-ignore
            contact: getters.getInquiryContact,
            // @ts-ignore
            travellers: rootState.checkout.personalData.persons,
            paymentMethod: state.inquiry.paymentMethod,
            voucher: state.inquiry.voucher,
            additionalCustomerField: state.inquiry.additionalCustomerField,
            comment: toString(inquiryComments),
            consent: {
                journaway: state.dataPolicy.consent,
                organizer: organizerAgreement,
                whitelabel: getters.getWhitelabelConsent,
                newsletter: state.inquiry.consent.newsletter
            }
        }

        const checkoutSummary = rootState.checkout.checkoutSummary;

        let inquiry: Inquiry|TourradarInquiry = {
            ...rawInquiry,
            ...checkoutSummary
        }

        if (rootGetters['checkout/isTourradar']) {
            inquiry = {
                ...inquiry,
                ...state.tourradar
            }
        }

        commit('setInquiry', inquiry);
    },
    async handleDealResponse({ commit, dispatch }, dealResponse: DealResponse) {
        await commit('setDealResponse', dealResponse);

        if (dealResponse.status === BookingStatus.PRICE_CHANGED) {
            dispatch('openBookingInfoOverlay');
            await dispatch('getPriceSummary');

            commit('disableCheckoutSubmitBtn', false);

            return;
        }
        if (dealResponse.status === BookingStatus.APPOINTMENTDATA_CHANGED) {
            dispatch('openBookingInfoOverlay');
            await dispatch('restoreDealRequest');

            commit('disableCheckoutSubmitBtn', false);

            return;
        }
        if (dealResponse.status === BookingStatus.SOLDOUT) {
            dispatch('openBookingInfoOverlay');

            return;
        }
        if (dealResponse.status === BookingStatus.ERROR) {
            dispatch('openErrorOverlay');

            throw new Error(`Error in handleDealResponse | Error code: 1703070029 | DealResponse status: ${dealResponse.status}`)
        }
    },
    openBookingInfoOverlay({ dispatch, rootState }) {
        dispatch('openOverlay', { selector: '.js-booking-info-overlay', modal: rootState.checkout.visibility.booking } );
    },
    async restoreDealRequest({ state, dispatch, commit }) {
        commit('restoreBookingRequest', state.dealResponse.bookingRequest);
        commit('restoreDealBookingStep', state.dealResponse.bookingStep);

        await dispatch('getPriceSummary');
    },
    async validateVoucher({ commit, dispatch, rootState }) {
        await postResponse(rootState.checkout.apiEndpoint, 'ValidateVoucher', rootState.checkout.apiToken, rootState.checkout.checkoutSummary, rootState.checkout.checkoutSettings.locale)
            .then(async (response: AxiosResponse) => {
                commit('setVoucherStatus', response.data.status);
                commit('setCurrentVoucher', rootState.checkout.checkoutSummary.voucher);

                await dispatch('getPriceSummary');
            })
            .catch(async (error: AxiosError) => {
                commit('setVoucherStatus', INVALID);
                commit('setCurrentVoucher', '');

                await dispatch('getPriceSummary');

                const responseStatus = error.response ? error.response.status : 'none';
                const message = `Error in validateVoucher | Error code: 1699620443 | HTTP status: ${responseStatus} | ${error.message}`;

                throw Error(message);
            });
    },
    clearVoucher({ commit }) {
        commit('clearVoucher');
        commit('resetVoucher');
    }
} as ActionTree<InquiryState, IStore>

function shouldCreateInquiry(rootState: any) {
    return rootState.checkout.checkoutSettings.bindingMode === 'nonBinding' || rootState.checkout.checkoutSettings.bindingMode === 'binding';
}
